import React, { useState } from 'react';
import { Trash2, Upload, Eye, Check } from 'lucide-react';
import { Bill, BillStatus } from '../services/billService';

interface BillsTableProps {
  bills: Bill[];
  onUpdateStatus: (id: number, status: BillStatus) => Promise<void>;
  onUploadProof: (id: number, file: File) => Promise<void>;
  onViewProof: (id: number) => void;
  onDelete: (id: number) => Promise<void>;
}

const BillsTable: React.FC<BillsTableProps> = ({
  bills,
  onUpdateStatus,
  onUploadProof,
  onViewProof,
  onDelete
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [uploadingBillId, setUploadingBillId] = useState<number | null>(null);

  const getStatusBadge = (status: BillStatus) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };

    const labels = {
      pending: 'Pendente',
      paid: 'Pago',
      overdue: 'Vencido',
      cancelled: 'Cancelado'
    };

    return (
      <span className={`px-2 py-1 ${badges[status]} text-xs font-semibold rounded`}>
        {labels[status]}
      </span>
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleFileUpload = async (billId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingBillId(billId);
    try {
      await onUploadProof(billId, file);
    } finally {
      setUploadingBillId(null);
      event.target.value = '';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assinante
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vencimento
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comprovante
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {bills.map((bill) => (
              <tr key={bill.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">
                    {bill.subscriptions?.subscribers?.name || 'N/A'}
                  </div>
                  {bill.subscriptions?.subscribers?.email && (
                    <div className="text-sm text-gray-500">
                      {bill.subscriptions.subscribers.email}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(bill.total_amount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(bill.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(bill.due_date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {bill.proof_payment_path ? (
                    <button
                      onClick={() => bill.id && onViewProof(bill.id)}
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Eye size={16} />
                      Ver
                    </button>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    {bill.status === 'pending' && (
                      <>
                        <button
                          onClick={() => bill.id && onUpdateStatus(bill.id, 'paid')}
                          className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Marcar como pago"
                        >
                          <Check size={18} />
                        </button>
                        <label
                          className={`p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer ${
                            uploadingBillId === bill.id ? 'opacity-50' : ''
                          }`}
                          title="Upload comprovante"
                        >
                          <Upload size={18} />
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => bill.id && handleFileUpload(bill.id, e)}
                            disabled={uploadingBillId === bill.id}
                          />
                        </label>
                      </>
                    )}
                    {deleteConfirm === bill.id ? (
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            if (bill.id) {
                              onDelete(bill.id);
                              setDeleteConfirm(null);
                            }
                          }}
                          className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                        >
                          Confirmar
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(bill.id || null)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {bills.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Nenhuma cobrança encontrada
        </div>
      )}
    </div>
  );
};

export default BillsTable;
